import { useLayoutEffect, useRef, useSyncExternalStore } from 'react';

/**
 * 🔴 SAYING SO WHEN THE CONNECTION IS GONE — design-system DS-11b3 (§1.7; F530).
 *
 * The back office and the portal save to the server as you work; with the connection gone every save fails, one
 * refusal at a time, and nothing said why. This is a pill in the bar while the browser is offline: "Offline — changes
 * will not save until the connection returns". Online it draws nothing to see. The till keeps its own queue state
 * (`Handheld.tsx`): it sells offline, so "will not save" would be untrue there, and it does not draw this.
 *
 * ⚠️ THE LIVE REGION IS ALWAYS THERE. A `role="status"` inserted together with its text is not announced by every
 * screen reader — the region must exist before its content changes. So the empty `span.wb-online` stays in the bar
 * (taken out of the flex row by `.wb-online:empty`, components.css), and only the pill inside it comes and goes.
 *
 * `navigator.onLine` through `useSyncExternalStore`: the `online` and `offline` events, no polling. "Online" means a
 * network, not the server: a server that is down still shows the screens' own errors (ErrorState), which say so.
 *
 * Borrowed by the portal by its own file (`check-portal-bundle.mjs` BORROWABLE). The words are a prop, so this imports
 * no dictionary: the back office passes `t(lang, …)`, the portal the English default (O-8).
 *
 * 🔴 OFFLINE, `--bar-h` IS THE BAR'S HEIGHT (DS-11b3 review, WCAG 2.4.11). The pill is the bar's extra row (app.css),
 * and `--bar-h` (tokens.css) feeds `scroll-padding-top` and the sticky table wrap: left at its online value, a field
 * that focus, an anchor jump or `scrollIntoView` brought to the top stopped under the taller bar, wholly hidden
 * (157px of bar over a 115px token on a 375px phone). So while the pill is drawn the root's `--bar-h` is the bar's
 * measured height (never less than the stylesheet's step, and left alone where that step is 0: the bar does not
 * stick there), kept by a ResizeObserver on the bar and window resizes; back online the stylesheet's value returns.
 */
export const OFFLINE = 'Offline — changes will not save until the connection returns';

function subscribe(changed: () => void) {
  window.addEventListener('online', changed);
  window.addEventListener('offline', changed);
  return () => {
    window.removeEventListener('online', changed);
    window.removeEventListener('offline', changed);
  };
}

export function OnlinePill({ label = OFFLINE }: { label?: string }) {
  const online = useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    () => true,
  );
  const region = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const bar = online ? null : region.current?.closest('.app-bar');
    if (!bar) return;
    const root = document.documentElement;
    const fit = () => {
      root.style.removeProperty('--bar-h');
      const step = parseFloat(getComputedStyle(root).getPropertyValue('--bar-h')) || 0;
      if (step > 0) root.style.setProperty('--bar-h', `${Math.max(step, Math.ceil(bar.getBoundingClientRect().height))}px`);
    };
    fit();
    const seen = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    seen?.observe(bar);
    window.addEventListener('resize', fit);
    return () => {
      seen?.disconnect();
      window.removeEventListener('resize', fit);
      root.style.removeProperty('--bar-h');
    };
  }, [online]);
  // The bar it sits in says its language (`header lang`, DS-4), so the pill needs no `lang` of its own.
  return (
    <span ref={region} className="wb-online" role="status">
      {online ? null : <span className="wb-online__pill">{label}</span>}
    </span>
  );
}
